import { useEffect, useState } from "react"

import { InfoOutlineIcon } from "@chakra-ui/icons"
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react"

import { useAppSelector } from "app/hooks"
import { useImportPolling } from "hooks/useImportPolling"
import { selectCurrentPmc } from "store/auth/authSlice"
import { getCookie, setCookie } from "utils/cookies"

const BACKGROUND_COOKIE_KEY = "background_import_polling"

const ImportProgressBanner = () => {
  const currentPmc = useAppSelector(selectCurrentPmc)
  const pmcId = currentPmc?.pmc_id || ""
  const pmsType = currentPmc?.pms_system || ""

  const [shouldPoll, setShouldPoll] = useState(false)
  const [wasCompleted, setWasCompleted] = useState(false)
  const [pollingInterval, setPollingInterval] = useState(10000)

  // Verify cookie on mount and periodically
  useEffect(() => {
    const checkCookie = () => {
      if (getCookie(BACKGROUND_COOKIE_KEY) === "true") {
        setShouldPoll(true)
      } else {
        setShouldPoll(false)
      }
    }
    checkCookie()
    const interval = setInterval(checkCookie, 5000)
    return () => clearInterval(interval)
  }, [])

  const { isCompleted, isFailed, error, steps } = useImportPolling({
    pmcId: shouldPoll && !wasCompleted ? pmcId : "",
    pmsType: shouldPoll && !wasCompleted ? pmsType : "",
    intervalMs: pollingInterval,
  })

  useEffect(() => {
    if (isCompleted) {
      setWasCompleted(true)
    }
  }, [isCompleted])

  // If the only active processing step is calendar_sync, the interval immediately switches to 15 minutes
  useEffect(() => {
    if (steps && steps.length > 0) {
      const active = steps.filter(
        (s) => s.status === "IN_PROGRESS" || s.status === "PENDING",
      )
      if (active.length === 1 && active[0].id === "calendar_sync") {
        setPollingInterval(15 * 60 * 1000) // 15min
      } else {
        setPollingInterval(10000) // 10sec
      }
    }
  }, [steps])

  useEffect(() => {
    if (shouldPoll && (isCompleted || isFailed || error)) {
      // Clear the cookie by setting it to a relative expiry of -1 days
      setCookie(BACKGROUND_COOKIE_KEY, "false", -1)

      // Instantly hide the banner on failure
      if (isFailed || error) {
        setShouldPoll(false)
      }
    }
  }, [isCompleted, isFailed, error, shouldPoll])

  if (!shouldPoll && !wasCompleted) return null

  if (wasCompleted) {
    return (
      <Box bg='green.500' color='white' px={4} py={2} w='full' zIndex={1000}>
        <Flex align='center' justify='center' gap={4}>
          <InfoOutlineIcon boxSize={5} />
          <Text fontSize='sm' fontWeight='medium'>
            Import completely finished! Refresh to see the latest data.
          </Text>
          <Button
            size='xs'
            colorScheme='whiteAlpha'
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </Flex>
      </Box>
    )
  }

  // Display dynamically which steps are still processing
  const processingSteps = steps
    .filter((s) => s.status === "IN_PROGRESS" || s.status === "PENDING")
    .map((s) => s.label)

  const processingMessage =
    processingSteps.length > 0
      ? `Background import in progress: ${processingSteps.join(", ")}...`
      : "Background import in progress..."

  // calendar_sync is still processing
  const calendarSyncStep = steps.find(
    (s) =>
      s.id === "calendar_sync" &&
      (s.status === "IN_PROGRESS" || s.status === "PENDING"),
  )

  return (
    <>
      <Box bg='blue.500' color='white' px={4} py={2} w='full' zIndex={1000}>
        <Flex align='center' justify='center' gap={4}>
          <Spinner size='sm' color='white' />
          <Text fontSize='sm' fontWeight='medium'>
            {processingMessage}
          </Text>
        </Flex>
      </Box>
      {calendarSyncStep?.message && (
        <Box bg='#a9c5e3' color='white' px={4} py={2} w='full' zIndex={1000}>
          <Flex align='center' justify='center' gap={4}>
            <Spinner size='sm' color='white' />
            <Text fontSize='sm' fontWeight='medium'>
              {calendarSyncStep.message}
            </Text>
          </Flex>
        </Box>
      )}
    </>
  )
}

export default ImportProgressBanner
